import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Empty } from "../components/common";
import { toast } from "sonner";
import { UserX, Trash2, Loader2, AlertTriangle } from "lucide-react";

export default function AdminOrphans() {
  const [orphans, setOrphans] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orphans");
      setOrphans(data);
      setSelected({});
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const allChecked = orphans.length > 0 && orphans.every((o) => selected[o.id]);
  const toggleAll = () => {
    if (allChecked) setSelected({});
    else setSelected(Object.fromEntries(orphans.map((o) => [o.id, true])));
  };
  const chosen = orphans.filter((o) => selected[o.id]).map((o) => o.id);

  const purge = async () => {
    if (chosen.length === 0) return;
    if (!window.confirm(`Permanently delete ${chosen.length} orphan account(s)? This cannot be undone.`)) return;
    setPurging(true);
    try {
      const { data } = await api.post("/admin/orphans/purge", { user_ids: chosen });
      toast.success(`Purged ${data.deleted} account(s)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not purge accounts");
    } finally {
      setPurging(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Administrator" title="Orphan Accounts"
        subtitle="SMME sign-ups that never created a company profile. Select which to remove." />

      <div className="flex items-center gap-2 mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        Accounts flagged as "has responses" have submitted opportunity responses — review carefully before purging.
      </div>

      {loading ? (
        <Loader2 className="h-6 w-6 animate-spin text-[#00427A]" />
      ) : orphans.length === 0 ? (
        <Empty icon={UserX} title="No orphan accounts" subtitle="Every SMME has started a company profile." />
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
              <input data-testid="orphan-select-all" type="checkbox" checked={allChecked} onChange={toggleAll}
                className="h-4 w-4 accent-[#00427A]" />
              Select all ({orphans.length})
            </label>
            <button data-testid="orphan-purge-button" onClick={purge} disabled={chosen.length === 0 || purging}
              className="inline-flex items-center gap-2 text-sm font-semibold bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md disabled:opacity-40 disabled:cursor-not-allowed">
              {purging ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Purge Selected ({chosen.length})
            </button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-left text-slate-500">
              <tr>
                <th className="px-5 py-3"></th>
                <th className="px-5 py-3 font-semibold">Name</th>
                <th className="px-5 py-3 font-semibold">Email</th>
                <th className="px-5 py-3 font-semibold">Signed up</th>
                <th className="px-5 py-3 font-semibold">Activity</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {orphans.map((o) => (
                <tr key={o.id} data-testid={`orphan-row-${o.id}`} className="hover:bg-slate-50">
                  <td className="px-5 py-4">
                    <input data-testid={`orphan-checkbox-${o.id}`} type="checkbox" checked={!!selected[o.id]}
                      onChange={() => toggle(o.id)} className="h-4 w-4 accent-[#00427A]" />
                  </td>
                  <td className="px-5 py-4 font-medium text-slate-900">{o.name || "—"}</td>
                  <td className="px-5 py-4 text-slate-600">{o.email}</td>
                  <td className="px-5 py-4 text-slate-500">{(o.created_at || "").slice(0, 10)}</td>
                  <td className="px-5 py-4">
                    {o.has_responses ? (
                      <span className="text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded">Has responses</span>
                    ) : (
                      <span className="text-xs text-slate-400">No activity</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
